/**
 * #719 — unwrap the message an edge function actually returned.
 *
 * supabase.functions.invoke throws FunctionsHttpError for any non-2xx, and its
 * `.message` is a fixed string ("Edge Function returned a non-2xx status code").
 * The function's own `{ error: "..." }` body is only on `.context`, the raw
 * Response. Callers that do `throw new Error(error.message)` therefore show the
 * operator nothing useful — "invite sent but the role was not granted" and
 * "invite failed entirely" look identical.
 */
export async function edgeFunctionErrorMessage(
  error: unknown,
  defaultMessage = "Request failed",
): Promise<string> {
  const fallback = error instanceof Error && error.message ? error.message : defaultMessage;
  const context = (error as { context?: unknown } | null)?.context;
  if (!(context instanceof Response)) return fallback;
  try {
    const body = (await context.clone().json()) as { error?: unknown } | null;
    // The status carries information the message alone doesn't: a 403 means the
    // call was refused, a 500 from manage-users means the account was created and
    // only the role write failed. The operator needs to tell those apart.
    if (body && typeof body.error === "string" && body.error.trim()) {
      return `${body.error} (HTTP ${context.status})`;
    }
  } catch {
    // Non-JSON body (an HTML error page, an empty 502) — fall through to the
    // status-only form below, which is the one case where the status is the only
    // thing we know.
  }
  return `${fallback} (HTTP ${context.status})`;
}
